// ════════════════════════════════════════════════════════════════════════
// LeadsGrid.tsx — Tabla de control de Leads (panel administrativo)
// ════════════════════════════════════════════════════════════════════════
// Consulta GET /leads (protegido con JWT + rol "admin" vía
// leadService.getLeads) y muestra los resultados ya ordenados por fecha de
// creación descendente — el backend los entrega así, aquí no se reordena.
//
// Bilingüe: reusa las mismas etiquetas de i18n/translations.ts (t.form.labelX)
// que ya usa el formulario público, así los headers de columna quedan
// garantizados a coincidir con las preguntas reales en ambos idiomas. Los
// strings propios del panel (Cargando/Estado/Idioma, etc.) viven en COPY
// acá mismo — son términos de UI administrativa, no contenido de
// marketing, así que no hace falta sumarlos al diccionario de la landing
// (que además ya tiene errores de tipos preexistentes, sin relación a
// este cambio). Cambiar el idioma con el toggle del Header (useLanguage)
// actualiza todo esto automáticamente, incluyendo qué campo bilingüe
// (selected.es/en) se muestra de cada lead.
//
// Ojo: `lead.language` (columna "Idioma") es el idioma en el que el
// VISITANTE mandó ese formulario — un dato guardado por lead, independiente
// de `lang` (el idioma que vos, como admin, tenés seleccionado ahora mismo
// en el Header). Son dos cosas distintas a propósito.
// ════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { getLeads } from '@/infrastructure/http/leadsApiClient';
import { useAuth } from '@/shared/context/AuthContext';
import { useLanguage } from '@/shared/context/LanguageContext';
import type { LeadListItem } from '@/infrastructure/http/leadsApiClient.types';
import type { Language } from '@/shared/i18n/translations';
import { Tag } from '@/shared/ui/Tag/Tag';
import styles from './LeadsGrid.module.css';

type LoadState = 'loading' | 'success' | 'error';

const COPY: Record<Language, {
  dateHeader: string;
  languageHeader: string;
  statusHeader: string;
  languageValue: Record<Language, string>;
  statusValue: Record<LeadListItem['status'], string>;
  loading: string;
  errorPrefix: string;
  empty: string;
}> = {
  es: {
    dateHeader: 'Fecha',
    languageHeader: 'Idioma',
    statusHeader: 'Estado',
    languageValue: { es: 'Español', en: 'Inglés' },
    statusValue: { New: 'Nuevo', Contacted: 'Contactado', Closed: 'Cerrado' },
    loading: 'Cargando leads…',
    errorPrefix: 'No se pudieron cargar los leads:',
    empty: 'Todavía no hay leads registrados.',
  },
  en: {
    dateHeader: 'Date',
    languageHeader: 'Language',
    statusHeader: 'Status',
    languageValue: { es: 'Spanish', en: 'English' },
    statusValue: { New: 'New', Contacted: 'Contacted', Closed: 'Closed' },
    loading: 'Loading leads…',
    errorPrefix: 'Could not load leads:',
    empty: 'No leads registered yet.',
  },
};

const STATUS_VARIANT: Record<LeadListItem['status'], 'amber' | 'cyan' | 'green'> = {
  New: 'amber',
  Contacted: 'cyan',
  Closed: 'green',
};

// El ":" al final de las etiquetas del formulario (ej. "Contacto:") queda
// raro como header de tabla — se recorta solo para mostrarlo acá.
const stripColon = (label: string): string => label.replace(/:$/, '');

export function LeadsGrid() {
  const { token } = useAuth();
  const { t, lang } = useLanguage();
  const copy = COPY[lang];
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);

  const formatDate = (iso: string): string =>
    new Date(iso).toLocaleString(lang === 'en' ? 'en-US' : 'es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  useEffect(() => {
    // ProtectedRoute ya garantiza isAuthenticated, pero el token puede
    // tardar un tick en poblarse en el primer render — sin esto, TypeScript
    // se queja de pasar `string | null` donde getLeads espera `string`.
    if (!token) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setState('loading');
      try {
        const data = await getLeads(token);
        if (!cancelled) {
          setLeads(data);
          setState('success');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error desconocido');
          setState('error');
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state === 'loading') {
    return <p className={styles.status}>{copy.loading}</p>;
  }

  if (state === 'error') {
    return (
      <p className={`${styles.status} ${styles.statusError}`}>
        {copy.errorPrefix} {error}
      </p>
    );
  }

  if (leads.length === 0) {
    return <p className={styles.status}>{copy.empty}</p>;
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{copy.dateHeader}</th>
            <th>{copy.languageHeader}</th>
            <th>{stripColon(t.form.labelCompany)}</th>
            <th>{stripColon(t.form.labelSector)}</th>
            <th>{stripColon(t.form.labelNeed)}</th>
            <th>{stripColon(t.form.labelOwnership)}</th>
            <th>{stripColon(t.form.labelUsers)}</th>
            <th>{stripColon(t.form.labelProblem)}</th>
            <th>{stripColon(t.form.labelTimeline)}</th>
            <th>{stripColon(t.form.labelBudget)}</th>
            <th>{stripColon(t.form.labelPhone)}</th>
            <th>{stripColon(t.form.labelEmail)}</th>
            <th>{stripColon(t.form.labelContactTime)}</th>
            <th>{stripColon(t.form.labelPromoCode)}</th>
            <th>{copy.statusHeader}</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr key={lead.leadId}>
              <td>{formatDate(lead.createdAt)}</td>
              <td>{copy.languageValue[lead.language]}</td>
              <td>{lead.company.value}</td>
              <td>{lead.sector.selected[lang]}</td>
              <td>{lead.need.selected[lang]}</td>
              <td>{lead.ownership.selected[lang]}</td>
              <td>{lead.users.selected[lang]}</td>
              <td className={styles.problemCell} title={lead.problem.value}>
                {lead.problem.value}
              </td>
              <td>{lead.timeline.selected[lang]}</td>
              <td>{lead.budget.selected[lang]}</td>
              <td>{lead.phone.value}</td>
              <td>{lead.email.value}</td>
              <td>{lead.contactTime.selected[lang]}</td>
              <td>{lead.promoCode.value || '—'}</td>
              <td>
                <Tag variant={STATUS_VARIANT[lead.status]}>{copy.statusValue[lead.status]}</Tag>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
